import { useCallback, useEffect, useState } from "react";
import type { LaneId } from "@/components/services/LaneChooser";

const KEY = "svc_lane";
const EVENT = "hireeva:services-lane";

function read(): LaneId | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.sessionStorage.getItem(KEY);
    return v === "audit" || v === "build" || v === "run" ? v : null;
  } catch {
    return null;
  }
}

/**
 * Services lane selection, persisted for the session so the choice survives
 * scrolling back up and is readable by the sticky bar, closing CTA and forms.
 */
export function useServicesLane() {
  const [lane, setLaneState] = useState<LaneId | null>(() => read());

  useEffect(() => {
    const onSync = () => setLaneState(read());
    window.addEventListener(EVENT, onSync);
    return () => window.removeEventListener(EVENT, onSync);
  }, []);

  const setLane = useCallback((next: LaneId | null) => {
    try {
      if (next) window.sessionStorage.setItem(KEY, next);
      else window.sessionStorage.removeItem(KEY);
    } catch {
      /* private mode — in-memory only */
    }
    setLaneState(next);
    window.dispatchEvent(new CustomEvent(EVENT));
  }, []);

  return { lane, setLane };
}
